import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Copy, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import EditRowSheet from "@/components/EditRowSheet";

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

const STATUS_LABEL = {
  open: "Open", assigned: "Assigned", in_progress: "In Progress",
  on_hold: "On Hold", resolved: "Resolved", closed: "Closed",
};
const STATUSES = Object.keys(STATUS_LABEL);
const PRIORITY_PILL = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-sky-100 text-sky-800",
  high: "bg-amber-100 text-amber-800",
  critical: "bg-red-100 text-red-800",
};
const STATUS_PILL = {
  open: "bg-red-50 text-red-700",
  assigned: "bg-sky-50 text-sky-700",
  in_progress: "bg-amber-50 text-amber-700",
  on_hold: "bg-slate-100 text-slate-700",
  resolved: "bg-emerald-50 text-emerald-700",
  closed: "bg-slate-100 text-slate-500",
};

export default function ServiceTickets({ projectId, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const empty = {
    project_id: projectId || "", equipment_id: "", client_name: "", client_email: "",
    complaint: "", priority: "medium", status: "open", assigned_to: "", sla_hours: 24, notes: "",
  };
  const [form, setForm] = useState(empty);
  const [resolveNotes, setResolveNotes] = useState("");

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get("/service-tickets", { params: projectId ? { project_id: projectId } : {} }),
      api.get("/projects"),
      api.get("/equipment"),
    ]);
    setRows(a.data); setProjects(b.data); setEquipment(c.data);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));
  const eqMap = Object.fromEntries(equipment.map((e) => [e.id, `${e.tag_no || ""} ${e.name}`.trim()]));

  const submit = async () => {
    try {
      await api.post("/service-tickets", {
        ...form,
        project_id: form.project_id || projectId || null,
        equipment_id: form.equipment_id || null,
        sla_hours: parseInt(form.sla_hours) || 24,
      });
      toast.success("Ticket created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const resolve = async () => {
    if (!resolveNotes.trim()) return toast.error("Add resolution notes");
    try {
      await api.post(`/service-tickets/${viewing.id}/resolve`, { resolution_notes: resolveNotes });
      toast.success("Ticket resolved. Feedback link is now active.");
      setResolveNotes(""); load();
      const r = await api.get(`/service-tickets/${viewing.id}`);
      setViewing(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this ticket?")) return;
    try { await api.delete(`/service-tickets/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const PUBLIC_BASE = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  const feedbackUrl = (token) => `${PUBLIC_BASE}/feedback/${token}`;

  const copyFeedback = (token) => {
    navigator.clipboard.writeText(feedbackUrl(token));
    toast.success("Feedback link copied to clipboard");
  };

  const formSheet = (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button data-testid="create-ticket-button" size={embedded ? "sm" : "default"} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
          <Plus size={14} className="mr-1" /> New Ticket
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[560px] sm:max-w-xl overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">New Service Ticket</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-6">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client name"><Input data-testid="ticket-client-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="AMC Client" /></Field>
            <Field label="Client email"><Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="ops@client.com" /></Field>
          </div>
          <Field label="Complaint / Description"><Textarea data-testid="ticket-complaint-input" rows={3} value={form.complaint} onChange={(e) => setForm({ ...form, complaint: e.target.value })} placeholder="AHU-3 not achieving set-point after coil clean…" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="SLA (hours)"><Input type="number" value={form.sla_hours} onChange={(e) => setForm({ ...form, sla_hours: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {!projectId && (
              <Field label="Project">
                <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Equipment (optional)">
              <Select value={form.equipment_id || "__none__"} onValueChange={(v) => setForm({ ...form, equipment_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {equipment.filter((e) => !form.project_id || e.project_id === form.project_id || !e.project_id).map((e) => <SelectItem key={e.id} value={e.id}>{e.tag_no || ""} {e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Assigned to"><Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} placeholder="Ravi Kumar" /></Field>
          <Field label="Internal notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button onClick={submit} data-testid="save-ticket-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Create Ticket</Button>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div data-testid="tickets-page">
      {!embedded && (
        <PageHeader
          title="Service Tickets"
          subtitle="Client complaints and work orders — SLA-tracked with client feedback loop"
          module="Service · SV"
          actions={formSheet}
        />
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="label-caps">Service tickets ({rows.length})</div>
          {formSheet}
        </div>
      )}
      <div className={embedded ? "" : "p-8"}>
        {rows.length === 0 ? (
          <EmptyState title="No tickets yet" description="Log a client complaint or a work-order. When you resolve it, share the auto-generated feedback link so the customer can rate the service." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="tickets-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">Ticket</TableHead>
                  <TableHead className="label-caps">Client</TableHead>
                  <TableHead className="label-caps">Complaint</TableHead>
                  <TableHead className="label-caps">Equipment</TableHead>
                  <TableHead className="label-caps">Priority</TableHead>
                  <TableHead className="label-caps">Status</TableHead>
                  <TableHead className="label-caps">Opened</TableHead>
                  <TableHead className="label-caps">Rating</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setViewing(t)} data-testid={`ticket-row-${t.id}`}>
                    <TableCell className="font-mono-num font-semibold text-[#EA580C]">{t.ticket_no}</TableCell>
                    <TableCell><div className="font-medium">{t.client_name || "—"}</div><div className="text-[10px] text-slate-500">{t.client_email || ""}</div></TableCell>
                    <TableCell className="text-slate-700 max-w-[280px] truncate">{t.complaint}</TableCell>
                    <TableCell className="text-[11px] text-slate-600">{eqMap[t.equipment_id] || "—"}</TableCell>
                    <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-widest ${PRIORITY_PILL[t.priority]}`}>{t.priority}</span></TableCell>
                    <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-widest ${STATUS_PILL[t.status]}`}>{STATUS_LABEL[t.status]}</span></TableCell>
                    <TableCell className="text-slate-600 font-mono-num text-xs">{fmtDate(t.opened_at)}</TableCell>
                    <TableCell className="text-[11px]">{t.feedback_submitted ? `${"★".repeat(t.feedback_rating || 0)} · NPS ${t.feedback_nps}` : <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        {t.status === "resolved" && !t.feedback_submitted && (
                          <button onClick={() => copyFeedback(t.feedback_token)} className="text-slate-500 hover:text-[#EA580C] p-1" title="Copy feedback link"><Copy size={13} /></button>
                        )}
                        <button onClick={() => setEditing(t)} data-testid={`edit-ticket-${t.id}`} title="Edit" className="text-slate-500 hover:text-[#EA580C] p-1"><PencilSimple size={13} /></button>
                        <button onClick={() => del(t.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={13} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Edit ticket */}
      {editing && (
        <EditRowSheet
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          title={`Edit Ticket · ${editing.ticket_no}`}
          endpoint={`/service-tickets/${editing.id}`}
          initial={editing}
          fields={[
            { name: "client_name", label: "Client name" },
            { name: "client_email", label: "Client email" },
            { name: "client_phone", label: "Client phone" },
            {
              name: "priority", label: "Priority", type: "select",
              options: [
                { value: "low", label: "Low" }, { value: "medium", label: "Medium" },
                { value: "high", label: "High" }, { value: "critical", label: "Critical" },
              ],
            },
            {
              name: "status", label: "Status", type: "select",
              options: STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
            },
            { name: "sla_hours", label: "SLA (hours)", type: "number" },
            { name: "assigned_to", label: "Assigned technician" },
            { name: "complaint", label: "Complaint", type: "textarea", colSpan: 2 },
            { name: "resolution", label: "Resolution notes", type: "textarea", colSpan: 2 },
          ]}
          onSaved={load}
        />
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <span className="font-mono text-[#EA580C]">{viewing.ticket_no}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${PRIORITY_PILL[viewing.priority]}`}>{viewing.priority}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${STATUS_PILL[viewing.status]}`}>{STATUS_LABEL[viewing.status]}</span>
                </DialogTitle>
                <div className="text-xs text-slate-500">{viewing.client_name || "—"} · Opened {fmtDate(viewing.opened_at)}</div>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div><div className="label-caps mb-1">Complaint</div><div className="text-slate-700 whitespace-pre-wrap">{viewing.complaint}</div></div>
                {viewing.equipment_id && <div><div className="label-caps mb-1">Equipment</div><div className="text-slate-700">{eqMap[viewing.equipment_id]}</div></div>}
                {viewing.assigned_to && <div><div className="label-caps mb-1">Assigned to</div><div className="text-slate-700">{viewing.assigned_to}</div></div>}
                {viewing.status !== "resolved" && viewing.status !== "closed" && (
                  <div className="border border-slate-200 p-3 rounded-sm bg-slate-50">
                    <div className="label-caps mb-2">Resolve ticket</div>
                    <Textarea rows={2} value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)} placeholder="How was the issue fixed?" />
                    <Button onClick={resolve} className="mt-2 rounded-sm bg-[#0F172A]" size="sm" data-testid="resolve-ticket-btn">Mark Resolved</Button>
                  </div>
                )}
                {viewing.resolution_notes && (
                  <div className="border border-emerald-200 bg-emerald-50 p-3 rounded-sm">
                    <div className="label-caps text-emerald-700 mb-1">Resolution</div>
                    <div className="text-slate-700 text-sm whitespace-pre-wrap">{viewing.resolution_notes}</div>
                    <div className="text-[10px] text-slate-500 mt-1">Resolved on {fmtDate(viewing.resolved_at)}</div>
                  </div>
                )}
                {viewing.resolved_at && !viewing.feedback_submitted && (
                  <div className="border border-sky-200 bg-sky-50 p-3 rounded-sm">
                    <div className="label-caps text-sky-700 mb-2">Client feedback link</div>
                    <div className="flex items-center gap-2">
                      <code className="text-[11px] bg-white px-2 py-1 border border-slate-200 rounded-sm flex-1 truncate">{feedbackUrl(viewing.feedback_token)}</code>
                      <Button size="sm" variant="outline" onClick={() => copyFeedback(viewing.feedback_token)}><Copy size={12} className="mr-1" /> Copy</Button>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-2">Share this with the client via email or WhatsApp — no login required to submit.</div>
                  </div>
                )}
                {viewing.feedback_submitted && (
                  <div className="border border-emerald-300 bg-emerald-50 p-3 rounded-sm">
                    <div className="label-caps text-emerald-700 mb-1">Client feedback received</div>
                    <div className="text-lg text-amber-500">{"★".repeat(viewing.feedback_rating || 0)}<span className="text-slate-300">{"★".repeat(5 - (viewing.feedback_rating || 0))}</span></div>
                    <div className="text-[12px] text-slate-600 mt-1">NPS: <span className="font-mono-num font-semibold">{viewing.feedback_nps}</span></div>
                    {viewing.feedback_comment && <div className="text-slate-700 mt-2 italic">&ldquo;{viewing.feedback_comment}&rdquo;</div>}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
